<template>
  <el-select
    ref="select"
    :value="customValue"
    :loading="customLoading"
    :remote="remote"
    :remote-method="customRemoteMethod"
    :class="{ 'hide-suffix': hideSuffix }"
    :disabled="disabled"
    :multiple="multiple"
    :collapse-tags="collapseTags"
    v-bind="props"
    @change="handleChange"
    @click.native="handleClick"
    v-on="listeners"
  >
    <slot></slot>
    <el-option
      v-for="item in customList"
      :key="item[valueKey]"
      :label="
        typeof item.formatLabel === 'function'
          ? item.formatLabel(item)
          : item[labelKey]
      "
      :value="numberValueKey ? +item[valueKey] : item[valueKey]"
      :disabled="disabledOption(item)"
    />
  </el-select>
</template>
<script>
import request from '@/utils/request'
import { debounce } from 'throttle-debounce'

export default {
  name: 'HxSelect',
  inject: {
    elForm: {
      default: '',
    },
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    api: [String, Function],
    reqData: {
      type: Object,
      default: () => ({}),
    },
    method: {
      type: String,
      default: 'post',
    },
    labelKey: {
      type: [String, Function],
      default: 'name',
    },
    valueKey: {
      type: String,
      default: 'code',
    },
    disabledOption: {
      type: Function,
      default: () => false,
    },
    formatLabel: Function,
    remote: Boolean,
    initQuery: {
      type: String,
      default: '',
    },
    numberValueKey: {
      type: Boolean,
      default: false,
    },
    // 第一次加载选项后是否自动选中第一条
    defaultFirst: Boolean,
    multiple: Boolean,
    collapseTags: {
      type: Boolean,
      default: false,
    },
    value: null,
    loading: {
      type: Boolean,
      default: false,
    },
    watchReqData: {
      type: Boolean,
      default: false,
    },
    showSuffix: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    lazy: {
      type: Boolean,
      default: false,
    },
    disabledLabel: [String, Number],
  },
  data() {
    return {
      remoteLoading: false,
      list: [],
      lazyLoading: false,
      debounceSetList: debounce(300, (val) => this.setList(val)),
      defaultProps: {
        size: 'small',
        clearable: true,
        filterable: true,
        placeholder: '请选择',
      },
    }
  },
  computed: {
    selectDisabled() {
      return this.disabled || (this.elForm || {}).disabled
    },
    customValue() {
      if (this.selectDisabled && this.disabledLabel) {
        return this.disabledLabel
      }

      return this.value
    },
    listeners() {
      const events = { ...this.$listeners }

      delete events.change

      return events
    },
    customLoading() {
      if (this.remote) {
        return this.remoteLoading
      }

      if (this.lazy) {
        return this.lazyLoading
      }

      return this.loading
    },
    props() {
      return Object.assign({}, this.defaultProps, this.$attrs)
    },
    customList() {
      return this.list.map((item) => {
        const obj = Object.assign({}, item)

        if (this.formatLabel) {
          obj.formatLabel = this.formatLabel
        }

        if (obj.value) {
          obj.optionCode = obj.value
        }

        if (obj.label) {
          obj.optionName = obj.label
        }

        return obj
      })
    },
    hideSuffix() {
      if (!this.showSuffix) {
        return true
      }

      if (this.disabled) {
        return true
      }

      if (this.$refs.select && this.$refs.select.selectDisabled) {
        return true
      }

      return false
    },
    placeholder() {
      return this.remote ? '请输入关键字检索' : '请选择'
    },
  },
  watch: {
    options(val, oldVal) {
      this.list = val

      this.setSelectedItem()
    },
    reqData(val, oldVal) {
      // 添加自动联动效果。由于在模板里直接添加对象字面量，父组件每次发生更新reqData属性都会重新赋值，所以都会执行一遍这里
      if (this.watchReqData && JSON.stringify(val) !== JSON.stringify(oldVal)) {
        this.$emit('input', '')
        this.setList(null)
      }
    },
    initQuery: {
      immediate: true,
      handler(val, oldVal) {
        if (val && val !== oldVal) {
          this.debounceSetList(val)
        }
      },
    },
    value(val, oldVal) {
      this.setSelectedItem()
    },
    lazy(val, oldVal) {
      if (val === false && val !== oldVal) {
        this.setList()
      }
    },
  },
  created() {
    if (!this.remote && !this.lazy) {
      this.setList()
    }
  },
  updated() {
    if (this.$refs.select.selectDisabled) {
      this.defaultProps.placeholder = ''
    } else {
      this.defaultProps.placeholder = this.placeholder
    }
  },
  mounted() {
    if (this.$refs.select.selectDisabled) {
      this.defaultProps.placeholder = ''
    } else {
      this.defaultProps.placeholder = this.placeholder
    }
  },
  methods: {
    async setList(query) {
      if (this.api) {
        const config = {
          url: this.api,
          method: this.method,
          loading: false,
        }

        let reqData = Object.assign({}, this.reqData)

        if (this.remote) {
          reqData = JSON.parse(
            JSON.stringify(reqData).replace('$query', query || ''),
          )
        }

        if (this.method.toLocaleLowerCase() === 'get') {
          config.params = reqData
        } else {
          config.data = reqData
        }

        const res = await this.request(config)

        let data =
          (res?.data && res?.data?.orgList) ||
          (res?.data && res?.data?.list) ||
          (res?.data && res?.data?.data) ||
          res?.data ||
          []

        if (data.hasOwnProperty('data')) {
          data = data.data || []
        }

        if (Object.prototype.toString.call(data) === '[object Object]') {
          this.list = Object.keys(data).map((k) => ({
            value: k,
            label: data[k],
          }))
        } else {
          this.list = data
        }

        this.$emit('update:options', this.list)
      } else {
        this.list = this.options || []
      }

      if (this.defaultFirst && this.list && this.list[0]) {
        this.selectedFirst()
      }

      this.$nextTick(() => {
        this.setSelectedItem()
      })
    },
    request(config) {
      if (typeof config.url === 'function') {
        return config.url(config.data)
      } else {
        return request(config)
      }
    },
    setSelectedItem() {
      const item = this.getCurrentItem(this.value)

      if (item) {
        this.$emit('selected', item)
      } else {
        this.$emit('selected', {})
      }
    },
    getCurrentItem(val) {
      if (Array.isArray(val)) {
        return (this.customList || []).filter(
          (item) => val.indexOf(item[this.valueKey]) !== -1,
        )
      } else {
        return (this.customList || []).find(
          (item) => item[this.valueKey] === val,
        )
      }
    },
    handleChange(val) {
      const item = this.getCurrentItem(val)
      this.$emit('change', val, item)
    },
    async customRemoteMethod(query) {
      if (!query) {
        this.clearOptions()
        return
      }
      this.remoteLoading = true
      await this.setList(query)
      this.remoteLoading = false
    },

    getRefs() {
      return this.$refs.select
    },

    selectedFirst() {
      const firstVal = this.customList[0][this.valueKey]
      if (this.multiple) {
        this.$emit('input', [firstVal])
      } else {
        this.$emit('input', firstVal)
      }
    },

    clearOptions() {
      this.list = []
    },

    async handleClick() {
      if (this.lazy && !this.selectDisabled) {
        this.lazyLoading = true
        await this.setList()
        this.lazyLoading = false
      }
    },
  },
}
</script>
